import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HandleImgError from "../../../utils/HandleURLImgError";
import DraftsRevokeModal from "../../../common/DraftsRevokeModal/DraftsRevokeModal";
import { ReactComponent as EditIcon } from "../../../resources/icons/edit.svg";
import { ReactComponent as FlyingMail } from "../../../resources/icons/flying-mail.svg";
import { ReactComponent as DeleteIcon } from "../../../resources/icons/delete.svg";
import { ReactComponent as CheckIcon } from "../../../resources/icons/succuss-tick.svg";
import ToolTip from "../../../common/ToolTip/ToolTip";
import { FileImage, FileText } from "lucide-react";
import useNavigationStore from "../../../store/navigationStore";

const DraftsCredentialTemplateCard = (props) => {
  const {
    name,
    description,
    credential_template_id,
    issuer_credential_template_id,
    thumbnail_webp_url,
    navigate_url,
    removeCredentialFromList,
    removeActionButtons,
    isSelected,
    onSelect,
    template_type,
    programme_name,
    batch_name,
    programme_code,
    batch_code,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(null);
  const navigate = useNavigate();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const toggleModal = (id) => {
    setIsModalOpen(id);
  };

  const handleIssueCertificateClick = (e) => {
    e.stopPropagation();

    if (template_type === "Legacy") {
      const programme = currentProgramme || {
        programme_name: programme_name,
        programme_code: programme_code,
      };

      const batch = currentBatch || {
        batch_name: batch_name,
        batch_code: batch_code,
      };

      useNavigationStore.setState({
        currentProgramme: programme,
        currentBatch: batch,
      });

      navigate(
        `/issuer/workspace/batches/legacy-issuance/${issuer_credential_template_id}`,
        {
          state: {
            programme: programme,
            batch: batch,
            fromDrafts: true,
          },
        }
      );
    } else if (template_type === "SVG") {
      navigate(
        `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}`
      );
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (credential_template_id && navigate_url) {
      navigate(`${navigate_url}`);
    }
  };

  return (
    <div
      className={`flex flex-col gap-3 bg-cfSecondary ${
        isSelected
          ? "border-2 border-dashed border-cfPrimaryButton scale-105"
          : "border border-cfCardBorder"
      } ${
        credential_template_id &&
        "cursor-pointer hover:bg-cfCardHover hover:scale-105"
      } transition-transform duration-500 rounded-2xl overflow-hidden w-[19.125rem] relative`}
      name={credential_template_id}
      onClick={() => {
        if (onSelect) {
          onSelect(issuer_credential_template_id);
        } else if (credential_template_id && navigate_url) {
          navigate(`${navigate_url}`);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (onSelect) {
            onSelect(issuer_credential_template_id);
          } else if (credential_template_id && navigate_url) {
            navigate(`${navigate_url}`);
          }
        }
      }}
      role={credential_template_id ? "button" : undefined}
      tabIndex={credential_template_id ? 0 : -1}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 flex items-center justify-center z-10">
          <CheckIcon className="text-cfSecondary w-9 h-9" />
        </div>
      )}
      <div
        className={`flex flex-col ${
          removeActionButtons ? "h-[22rem]" : "h-full"
        } `}
      >
        <div className="flex flex-col h-fit">
          <div
            className={`flex items-center h-[12.5rem] border-b w-full bg-cfSecondary ${
              !thumbnail_webp_url && "w-full animate-pulse bg-zinc-300"
            }`}
          >
            <img
              src={
                (thumbnail_webp_url || thumbnail_webp_url === null) &&
                thumbnail_webp_url
              }
              onError={(e) => HandleImgError(e, thumbnail_webp_url)}
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>

          {template_type && (
            <span
              className={`text-xs px-2 py-1 rounded-full shrink-0 absolute top-3 right-2 ${
                template_type === "SVG"
                  ? "bg-[#EEF2FF] text-[#4F46E5]"
                  : "bg-[#ffe8cb] text-[#de881f]"
              }`}
            >
              {template_type === "SVG" ? (
                <div className="flex items-center gap-1">
                  <FileText className="h-3 w-3 text-cfPrimaryButton" />
                  <span>SVG</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <FileImage className="h-3 w-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG" />
                  <span>Legacy</span>
                </div>
              )}
            </span>
          )}
        </div>

        <div
          className={`flex flex-col gap-4 ${
            !removeActionButtons ? "h-[7rem]" : "h-[4rem]"
          } m-4`}
        >
          <div>
            <div
              className={`${
                removeActionButtons
                  ? "h-10 overflow-ellipsis font-medium text-lg"
                  : "text-cfHeadingText font-semibold text-xl"
              } `}
            >
              <p
                className={`line-clamp-1 ${
                  !name && "h-8 w-full animate-pulse bg-zinc-300 rounded-md"
                }`}
              >
                {name}
              </p>
            </div>
            <div
              className={`${removeActionButtons ? "flex flex-col gap-5" : ""}`}
            >
              <p
                className={`${
                  removeActionButtons ? "text-xs" : "text-base"
                } line-clamp-2 ${
                  !description &&
                  "h-8 w-full animate-pulse bg-zinc-300 text-gray-400 rounded-md"
                }`}
              >
                {description}
              </p>

              {removeActionButtons && (
                <div className="flex gap-2">
                  {programme_name && (
                    <ToolTip
                      component={
                        <p
                          className="text-xs bg-cfCardTagFill text-cfCardTagText font-medium 
                        group-hover:bg-cfSignInSignUpOrText group-hover:text-cfSecondary 
                        max-w-[130px] truncate px-2 py-1 rounded-full h-fit"
                        >
                          {programme_name}
                        </p>
                      }
                      message={programme_name}
                      tooltipPosition="top-[-3rem]"
                      bgColor="#98A2B3"
                      tooltipWidth="max-w-[200px]"
                    />
                  )}
                  {batch_name && (
                    <ToolTip
                      component={
                        <p
                          className="text-xs bg-cfCardTagFill text-cfCardTagText font-medium 
                        group-hover:bg-cfSignInSignUpOrText group-hover:text-cfSecondary 
                        max-w-[130px] truncate px-2 py-1 rounded-full h-fit"
                        >
                          {batch_name}
                        </p>
                      }
                      message={batch_name}
                      tooltipPosition="top-[-3rem]"
                      bgColor="#98A2B3"
                      tooltipWidth="max-w-[200px]"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {!removeActionButtons && (
            <div className="grid grid-cols-3 divide-x divide-slate-600 divide-solid">
              <div className="flex justify-center items-center group">
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                      onClick={handleEditClick}
                    />
                  }
                  message={"Edit Template"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                className="flex justify-center items-center group"
                onClick={handleIssueCertificateClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleIssueCertificateClick(e);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={"Issue Certificate"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModal(issuer_credential_template_id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleModal(issuer_credential_template_id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <DeleteIcon
                      className="fill-cfSecondary transition-colors duration-500 hover:fill-cfIssuedCredentialTemplateCardStatusRevokedBG"
                      height={20}
                      width={20}
                    />
                  }
                  message={"Delete Draft"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#DC2626"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
            </div>
          )}

          {/* <div className="flex gap-1"> //! Can be used for tags in future
            {tagsList ? (
              tagsList.slice(0, 2).map((item, index) => (
                <p
                  key={index}
                  className=" text-xs bg-cfCardTagFill text-cfCardTagText font-medium group-hover:bg-cfTagFillHover group-hover:text-cfSecondary w-fit px-2 py-1 rounded-full h-fit"
                >
                  {item}
                </p>
              ))
            ) : (
              <>
                <p className="h-5 w-4/12 animate-pulse text-xs bg-zinc-300 px-2 py-1 rounded-full"></p>
                <p className="h-5 w-5/12 animate-pulse text-xs bg-zinc-300 px-2 py-1 rounded-full"></p>
              </>
            )}
          </div> */}
        </div>
      </div>
      <DraftsRevokeModal
        isOpen={isModalOpen !== null}
        onClose={() => setIsModalOpen(null)}
        onConfirm={() => {
          removeCredentialFromList(isModalOpen);
          setIsModalOpen(null);
        }}
        title="Delete Draft"
        description="Are you sure you want to delete this draft?"
      />
    </div>
  );
};
export default DraftsCredentialTemplateCard;
